import {AllOrNone} from '../enums/all-or-none';
import {Exchange, isExchangesSupportsIceberg} from '../enums/exchange';
import type {OrderAlgoType} from '../enums/order-algo-type';
import {OrderHandlinglInstruction} from '../enums/order-handling-instruction';
import {OrderRejectionReason} from '../enums/order-rejection-reason';
import {OrderStatus} from '../enums/order-status';
import {OrderTransactionType} from '../enums/order-transaction-type';
import {OrdType} from '../enums/order-type';
import {Side} from '../enums/side';
import {TimeInForce} from '../enums/time-in-force';
import {Company} from './company';
import {ExchangeAccount, ExchangeAccountId} from './exchange-account';
import {OrderAlgoParams} from './order-algo-params';
import {Security} from './security';
import {User} from './user';

export interface OrderState {

    /** Leaves quantity. */
    leavesQty: number;

    /** Cumulative quantity. */
    cumQty: number;

    /** Average price. */
    avgPx: number;

    /** Order rejection reason ID. */
    ordRejReason: OrderRejectionReason;

    /** Order rejection reason text. */
    ordRejReasonText: string;

    /** Order Status. */
    orderStatus: OrderStatus;
}

export interface Order {

    /** Unique ID. */
    id?: number;

    /** Order ID  */
    orderId?: string;

    /** Client Order ID. */
    clOrdId?: string;

    /** Orig unique ID. */
    origId?: number;

    /** Original Client Order ID. */
    origClOrdId: string;

    /** Security. */
    security: Security;

    /** Handling instruction. */
    handlInst?: OrderHandlinglInstruction;

    /** Minimum execution quantity. */
    minQty?: number;

    /** Side. */
    side: Side;

    /** Order State. */
    state: OrderState;

    /**
     * All or None.
     * Note: It absent in the API's Order model, used on UI only (means minQty = orderQty).
     */
    allOrNone?: AllOrNone;

    /** Creation time. */
    transactTime?: string;

    /** Quantity. */
    orderQty: number;

    /**
     * [TRAD-101] Quantity in quote asset.
     * Should be used only if the exchange natively supports it.
     */
    orderQuoteQty: number;

    /**
     * Internal temporary field, is used to force passing qty in base asset,
     * regardless of which asset in native for the {@link orderQty}.
     * This field should never be passed to API, and API doesn't know this field.
     */
    _orderBaseQty?: number;

    /** Order type. */
    ordType: OrdType;

    /** Exchange Account */
    account: ExchangeAccount;

    /** Company */
    company: Company;

    /** User */
    initiator?: User;

    /** Transaction type. */
    transactionType: OrderTransactionType;

    /** Limit price. */
    price: number;

    /** Stop Limit price. */
    stopPx?: number;

    /** Time in Force. */
    timeInForce: TimeInForce;

    /** Text. */
    text?: string;

    /** Execution instructions. */
    execInst: string;

    /** Indicates whether the broker is to locate the stock in conjunction with a short sell order. */
    locateReqd?: boolean;

    /** Identifies which broker has loaned the stock to settle the short sale. */
    locateBroker?: string;

    /** Amount (signed) added to the price of the peg for a pegged order. */
    pegDifference?: number;

    /** Account ID. */
    accountId: ExchangeAccountId;

    /** ID of the {@link User}, who placed the order. */
    initiatorId?: number;

    /** ID of the Smart order, which is parent of this order. */
    smartOrigId: string;

    /** Leverage */
    leverage: number;

    portfolioId?: number;
    investmentEntityId?: number;

    /**
     * This field may be undefined in some cases (the cases are unknown at the moment).
     * It allows API to tell UI, that the order should be treated as Filled,
     * regardless of it's orderStatus.
     */
    filled?: boolean;

    icebergQty?: number;

    algoType: OrderAlgoType;
    algoOrigId: number; // Refers to Order.id
    algoParams: OrderAlgoParams;
}

export interface NewOrder extends Partial<Order> {

    /** Security ID. */
    securityId?: number;

    /** Side. */
    side: Side;

    /** Quantity. */
    orderQty: number;

    /**
     * [TRAD-101, TRAD-114] Quantity in quote asset.
     * Should be used only if the exchange natively supports it.
     */
    orderQuoteQty?: number;

    /**
     * Internal temporary field, is used to force passing qty in base asset,
     * regardless of which asset in native for the {@link orderQty}.
     */
    _orderBaseQty?: number;

    /** Order type. */
    ordType?: OrdType;

    /** Transaction type. */
    transactionType: OrderTransactionType;

    /** Limit price. */
    price?: number;

    /** Stop Limit price. */
    stopPx?: number;

    /** Time in Force. */
    timeInForce: TimeInForce;

    /** Account ID. */
    accountId?: ExchangeAccountId;

    portfolioId?: number;
    investmentEntityId?: number;

    leverage: number;

    algoType?: OrderAlgoType;
    algoParams?: OrderAlgoParams;
}

export interface ReplaceOrCancelOrder extends NewOrder {

    /** Original Client Order ID. */
    origClOrdId: string;
}

export const isOrderIcebergAllowed = (exchange: Exchange, orderType: OrdType): boolean =>
    exchange && isExchangesSupportsIceberg(exchange)
    && (
        orderType === OrdType.LIMIT
        || orderType === OrdType.STOP_LIMIT
    );
